import React from 'react';
import PropTypes from 'prop-types';
import { Bar, XAxis, YAxis, Legend, ResponsiveContainer, LabelList, Tooltip, Line, ComposedChart } from 'recharts';
import { useTheme } from '@mui/material/styles';
import { convertToDayjs } from '../../../../utils/dateUtils';
import {
  axisStyles,
  xAxisProps,
  barChartMargins,
  barProps,
  barShape,
  responsiveContainerProps,
  tooltipProps,
} from './FinancialPerformanceChart.styles';
import { currencyFormatter } from '../../../../utils/helpers/formatting/currencyHelpers';

const FinancialPerformanceChart = ({ dailyTickets }) => {
  // Theme is used here instead of the styles file because it's a recharts component, which requires a theme prop. See the styles file for more information.
  const theme = useTheme();

  const chartData = dailyTickets.map((ticket) => ({
    date: convertToDayjs(ticket.date)?.format('ddd') || 'N/A',
    net: ticket.net || 0,
    cost: ticket.cost || 0,
    costPercentage: ticket.costPercentage || 0,
  }));

  const formatTooltipValue = (value, name) => {
    if (name === 'Labor Cost %') {
      return [`${value}%`, name];
    }
    // Convert cents to dollars for display
    const valueInDollars = value / 100;
    return [currencyFormatter(valueInDollars * 100), name];
  };

  return (
    <ResponsiveContainer {...responsiveContainerProps}>
      <ComposedChart data={chartData} margin={barChartMargins}>
        <XAxis dataKey="date" {...xAxisProps(theme)} style={axisStyles(theme)} />
        <YAxis yAxisId="left" tickFormatter={currencyFormatter} style={axisStyles(theme)} />
        <YAxis yAxisId="right" orientation="right" tickFormatter={(value) => `${value}%`} style={axisStyles(theme)} />
        <Tooltip {...tooltipProps(theme)} formatter={formatTooltipValue} />
        <Legend />
        <Bar
          yAxisId="left"
          dataKey="net"
          name="Net Sales"
          {...barProps(theme)}
          shape={barShape}
          isAnimationActive={false}
        >
          <LabelList dataKey="net" position="top" formatter={(value) => currencyFormatter(value)} />
        </Bar>
        <Bar
          yAxisId="left"
          dataKey="cost"
          name="Labor Cost"
          {...barProps(theme)}
          fill={theme.palette.error.main}
          shape={barShape}
          isAnimationActive={false}
        >
          <LabelList dataKey="cost" position="top" formatter={(value) => currencyFormatter(value)} />
        </Bar>
        <Line
          yAxisId="right"
          type="monotone"
          dataKey="costPercentage"
          name="Labor Cost %"
          stroke={theme.palette.warning.main}
          strokeWidth={2}
          dot={{ fill: theme.palette.warning.main }}
          isAnimationActive={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

FinancialPerformanceChart.propTypes = {
  dailyTickets: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      net: PropTypes.number,
      cost: PropTypes.number,
      costPercentage: PropTypes.number,
    })
  ).isRequired,
};

export default FinancialPerformanceChart;
